import { useState, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, X, ChevronRight, Tag } from 'lucide-react'
import { api } from '../lib/api'

// Multi-select target picker for scoping a discount code (1b) — modelled on
// DeepLinkInput's grouped searchable dropdown, but it collects MANY targets
// (categories / brands / products, mixed) as chips rather than one deep link.
// Each target is { kind, ref, label } where ref is the category id, the brand
// string, or the product id. Data sources are the same admin endpoints the
// deep-link picker uses (so the shapes match: products -> `name`, categories
// -> `name`, brands -> `/products/brands`).

export interface Target { kind: 'category' | 'brand' | 'product'; ref: string; label: string }

interface Category { id: string; slug: string; name: string }
interface Brand    { name: string; product_count: number }
interface Product  { id: string; name: string; brand: string | null }

export function DiscountTargetPicker({ targets, onChange }: { targets: Target[]; onChange: (t: Target[]) => void }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 250)
    return () => clearTimeout(t)
  }, [search])

  const { data: catData } = useQuery<{ categories: Category[] }>({
    queryKey: ['categories'], queryFn: () => api.get('/categories').then(r => r.data), staleTime: 60_000,
  })
  const { data: brandData } = useQuery<{ brands: Brand[] }>({
    queryKey: ['admin-brands'], queryFn: () => api.get('/products/brands').then(r => r.data), staleTime: 60_000,
  })
  const { data: prodData } = useQuery<{ products: Product[] }>({
    queryKey: ['discount-target-products', debounced],
    queryFn: () => api.get(`/products?search=${encodeURIComponent(debounced)}&limit=8`).then(r => r.data),
    enabled: debounced.trim().length > 1,
  })

  const has = (t: Target) => targets.some(x => x.kind === t.kind && x.ref === t.ref)
  const add = (t: Target) => { if (!has(t)) onChange([...targets, t]); setSearch('') }
  const remove = (i: number) => onChange(targets.filter((_, j) => j !== i))

  const q = search.trim().toLowerCase()
  const cats = (catData?.categories ?? []).filter(c => !q || c.name.toLowerCase().includes(q)).slice(0, 6)
  const brands = (brandData?.brands ?? []).filter(b => !q || b.name.toLowerCase().includes(q)).slice(0, 6)
  const products = prodData?.products ?? []
  const hasResults = cats.length > 0 || brands.length > 0 || products.length > 0

  return (
    <div ref={containerRef} className="relative space-y-2">
      {targets.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {targets.map((t, i) => (
            <span key={`${t.kind}:${t.ref}`} className="inline-flex items-center gap-1 bg-white/10 text-gray-200 text-xs px-2 py-1 rounded">
              {t.label}
              <button type="button" onClick={() => remove(i)} className="text-gray-400 hover:text-white"><X size={11} /></button>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 bg-[#111] border border-[#2e2e2e] rounded-lg px-3 py-2 cursor-text" onClick={() => setOpen(true)}>
        <Search size={13} className="text-gray-600 shrink-0" />
        <input
          value={search}
          onChange={e => { setSearch(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          placeholder="Search categories, brands, products…"
          className="flex-1 bg-transparent text-sm text-white placeholder-gray-600 focus:outline-none"
        />
      </div>

      {open && (
        <div className="absolute top-full mt-1 left-0 right-0 bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl shadow-2xl z-50 overflow-hidden max-h-72 overflow-y-auto">
          {cats.length > 0 && (
            <div>
              <p className="px-3 pt-2.5 pb-1 text-[10px] text-gray-600 uppercase tracking-wider">Categories</p>
              {cats.map(c => (
                <button key={c.id} type="button" onClick={() => add({ kind: 'category', ref: c.id, label: `Category: ${c.name}` })}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-white/5 text-left group">
                  <span className="text-sm text-white">{c.name}</span>
                  <ChevronRight size={13} className="text-gray-700 group-hover:text-gray-400" />
                </button>
              ))}
            </div>
          )}
          {brands.length > 0 && (
            <div>
              <p className="px-3 pt-2.5 pb-1 text-[10px] text-gray-600 uppercase tracking-wider border-t border-[#222] mt-1">Brands</p>
              {brands.map(b => (
                <button key={b.name} type="button" onClick={() => add({ kind: 'brand', ref: b.name, label: `Brand: ${b.name}` })}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-white/5 text-left group">
                  <span className="text-sm text-white">{b.name} <span className="text-gray-600 text-xs">· {b.product_count} {b.product_count === 1 ? 'product' : 'products'}</span></span>
                  <ChevronRight size={13} className="text-gray-700 group-hover:text-gray-400" />
                </button>
              ))}
            </div>
          )}
          {products.length > 0 && (
            <div>
              <p className="px-3 pt-2.5 pb-1 text-[10px] text-gray-600 uppercase tracking-wider border-t border-[#222] mt-1">Products</p>
              {products.map(p => (
                <button key={p.id} type="button" onClick={() => add({ kind: 'product', ref: p.id, label: p.name })}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-white/5 text-left group">
                  <span className="text-sm text-white">{p.name}{p.brand ? <span className="text-gray-600 text-xs"> · {p.brand}</span> : null}</span>
                  <ChevronRight size={13} className="text-gray-700 group-hover:text-gray-400" />
                </button>
              ))}
            </div>
          )}
          {!hasResults && (
            <p className="px-4 py-5 text-sm text-gray-600 text-center flex items-center justify-center gap-2">
              <Tag size={13} /> {debounced.trim().length > 1 ? 'No matches' : 'Type to search, or browse categories & brands'}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
